import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  LanguageEntry,
  LanguageLevel,
  LANGUAGE_LEVEL_OPTIONS,
  LanguageLevelOption,
} from '../../../../shared/constants/language-levels';
import {
  languageDisplayName,
  languageLabelKey,
  LanguageOption,
  sortLanguageOptions,
} from '../../../../shared/constants/languages';
import { SupportedLanguagesService } from '../../../../services/supported-languages.service';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { CiSelectComponent } from '../../../../../profiler-theme';

const I18N = 'personalData.languageLevels.';

@Component({
  selector: 'app-language-levels',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe, CiSelectComponent, MatIcon, MatIconButton],
  templateUrl: './language-levels.component.html',
  styleUrl: './language-levels.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LanguageLevelsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => LanguageLevelsComponent),
      multi: true,
    },
  ],
})
export class LanguageLevelsComponent
  implements ControlValueAccessor, Validator, OnDestroy
{
  private _editMode = true;

  @Input()
  get editMode(): boolean {
    return this._editMode;
  }
  set editMode(value: boolean) {
    if (this._editMode && !value) {
      this.invalidateAllUndos();
    }
    if (!this._editMode && value) {
      this.loadLanguages();
    }
    this._editMode = value;
  }

  @ViewChild('addBtn') addBtnRef!: ElementRef;
  @ViewChildren('languageSelect', { read: ElementRef }) languageSelectRefs!: QueryList<ElementRef>;
  @ViewChildren('levelSelect', { read: ElementRef }) levelSelectRefs!: QueryList<ElementRef>;

  languageOptions!: LanguageOption[];
  levelOptions!: readonly LanguageLevelOption[];

  rows!: FormArray<FormGroup>;
  groupErrorVisible = false;

  private onChange: (val: LanguageEntry[]) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  private undoInvalidators = new Set<() => void>();
  private undoSnackBar: MatSnackBarRef<TextOnlySnackBar> | null = null;
  private langChangeSub: Subscription;
  private languagesSub?: Subscription;
  private supportedCodes: string[] = [];
  private languagesLoaded = false;
  private languagesLoading = false;
  private readonly serverRejectedCodes = new Set<string>();

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private liveAnnouncer: LiveAnnouncer,
    private supportedLanguages: SupportedLanguagesService,
  ) {
    this.languageOptions = [];
    this.levelOptions = LANGUAGE_LEVEL_OPTIONS;
    this.rows = this.fb.array<FormGroup>([]);
    this.rows.valueChanges.subscribe(() => {
      this.onChange(this.toValue());
    });
    this.langChangeSub = this.translateService.onLangChange.subscribe(({ lang }) => {
      this.sortOptions(lang);
      // Neue Referenz, sonst rechnet app-ci-select den angezeigten Wert nicht neu.
      this.levelOptions = [...LANGUAGE_LEVEL_OPTIONS];
      this.cdr.markForCheck();
    });
    this.loadLanguages();
  }

  ngOnDestroy(): void {
    this.invalidateAllUndos();
    this.langChangeSub.unsubscribe();
    this.languagesSub?.unsubscribe();
  }

  displayLanguage = (option: LanguageOption): string =>
    languageDisplayName(
      option.code,
      key => this.translateService.instant(key),
      this.translateService.currentLang ?? 'de',
    );

  compareLanguage = (a: LanguageOption | null, b: LanguageOption | null): boolean =>
    !!a && !!b && a.code === b.code;

  displayLevel = (option: LanguageLevelOption): string =>
    this.translateService.instant(option.labelKey);

  isLanguageDisabled(rowIndex: number): (option: LanguageOption) => boolean {
    return (option: LanguageOption) => {
      return this.rows.controls.some((row, i) => {
        if (i === rowIndex) return false;
        return row.get('language')?.value?.code === option.code;
      });
    };
  }

  languageName(row: AbstractControl): string {
    const option = row.get('language')?.value as LanguageOption | null;
    return option ? this.displayLanguage(option) : '';
  }

  languageFieldLabel(index: number): string {
    return this.t('languageRowLabel', { row: index + 1 });
  }

  levelFieldLabel(row: AbstractControl, index: number): string {
    const language = this.languageName(row);
    return language
      ? this.t('levelForLanguage', { language })
      : this.t('levelRowLabel', { row: index + 1 });
  }

  removeButtonLabel(row: AbstractControl, index: number): string {
    const language = this.languageName(row);
    return language
      ? this.t('removeLanguageNamed', { language })
      : this.t('removeRow', { row: index + 1 });
  }

  languageErrorKey(row: AbstractControl): string {
    const language = row.get('language');
    if (language?.hasError('serverUnknownLanguage')) return 'profileLanguages.errors.unknownLanguage';
    return language?.hasError('unknownLanguage')
      ? I18N + 'unknownLanguage'
      : I18N + 'errors.languageRequired';
  }

  isNative(option: LanguageLevelOption | null): boolean {
    return option?.code === 'C2';
  }

  addRow(): void {
    const group = this.createRowGroup({ language: '' });
    this.rows.push(group);
    this.onChange(this.toValue());
    this.cdr.markForCheck();
    this.liveAnnouncer.announce(this.t('rowAdded'), 'polite');

    setTimeout(() => {
      const refs = this.languageSelectRefs?.toArray();
      if (refs?.length) {
        const last = refs[refs.length - 1];
        const el = last?.nativeElement as HTMLElement;
        const inner = el?.querySelector?.('[tabindex], input, button') as HTMLElement | null | undefined;
        (inner ?? el)?.focus();
      }
    });
  }

  removeRow(index: number): void {
    const row = this.rows.at(index);
    const language = this.languageName(row);
    const removed = row.getRawValue() as {
      language: LanguageOption | null;
      level: LanguageLevelOption | null;
    };
    const removedEntry: LanguageEntry = {
      language: (removed.language as LanguageOption)?.code ?? '',
      level: (removed.level as LanguageLevelOption)?.code as
        | LanguageLevel
        | undefined,
    };

    this.rows.removeAt(index);
    this.onChange(this.toValue());
    this.cdr.markForCheck();

    const removedKey = language ? 'removedLanguage' : 'removedRow';
    const restoredKey = language ? 'restoredLanguage' : 'restoredRow';
    const params: Record<string, unknown> = language ? { language } : { row: index + 1 };
    this.liveAnnouncer.announce(this.t(removedKey, params), 'polite');

    let called = false;
    let invalidate: () => void;

    const callback = () => {
      if (called) return;
      called = true;
      this.undoInvalidators.delete(invalidate);
      const group = this.createRowGroup(removedEntry);
      this.rows.insert(index, group);
      this.onChange(this.toValue());
      this.cdr.markForCheck();
      this.liveAnnouncer.announce(this.t(restoredKey, params), 'polite');
    };

    invalidate = () => { called = true; };
    this.undoInvalidators.add(invalidate);

    // Vorgelesen wird schon über den LiveAnnouncer.
    this.undoSnackBar = this.snackBar.open(this.t(removedKey, params), this.t('undoRemove'), {
      duration: 5000,
      politeness: 'off',
      panelClass: 'language-levels-undo-snackbar',
    });
    this.undoSnackBar.onAction().subscribe(callback);
  }

  get unknownCount(): number {
    return this.rows.controls.filter(
      row => (row.get('language')?.value as LanguageOption)?.unknown
    ).length;
  }

  get c2Count(): number {
    return this.rows.controls.filter(
      (row) =>
        (row.get('level')?.value as LanguageLevelOption)?.code === 'C2',
    ).length;
  }

  get groupErrorKey(): string {
    if (this.rows.length === 0) return I18N + 'errors.atLeastOne';
    if (this.c2Count === 0) return 'profileLanguages.errors.nativeRequired';
    const codes = this.rows.controls
      .map((row) => (row.get('language')?.value as LanguageOption)?.code)
      .filter(Boolean);
    const hasDuplicates = new Set(codes).size !== codes.length;
    if (hasDuplicates) return I18N + 'duplicateLanguage';
    return '';
  }

  showGroupErrors(): void {
    this.groupErrorVisible = true;
    this.rows.markAllAsTouched();
    this.cdr.markForCheck();
    this.focusFirstInvalid();
  }

  focusFirstInvalid(): void {
    setTimeout(() => {
      if (this.rows.length === 0) {
        (this.addBtnRef?.nativeElement as HTMLElement)?.focus();
        return;
      }
      const langRefs = this.languageSelectRefs?.toArray() ?? [];
      const levelRefs = this.levelSelectRefs?.toArray() ?? [];
      for (let i = 0; i < this.rows.length; i++) {
        if (this.rows.at(i).get('language')?.invalid) {
          const el = langRefs[i]?.nativeElement as HTMLElement;
          const inner = el?.querySelector?.('[tabindex], input, button') as HTMLElement | null | undefined;
          (inner ?? el)?.focus();
          return;
        }
      }
      for (let i = 0; i < this.rows.length; i++) {
        if (this.rows.at(i).get('level')?.invalid) {
          const el = levelRefs[i]?.nativeElement as HTMLElement;
          const inner = el?.querySelector?.('[tabindex], input, button') as HTMLElement | null | undefined;
          (inner ?? el)?.focus();
          return;
        }
      }
      const hasC2 = this.rows.controls.some(
        (row) =>
          (row.get('level')?.value as LanguageLevelOption)?.code === 'C2',
      );
      if (!hasC2 && levelRefs.length > 0) {
        const el = levelRefs[0]?.nativeElement as HTMLElement;
        const inner = el?.querySelector?.('[tabindex], input, button') as HTMLElement | null | undefined;
        (inner ?? el)?.focus();
      }
    });
  }

  hasGroupError(): boolean {
    return this.groupErrorKey !== '';
  }

  writeValue(value: LanguageEntry[] | null): void {
    this.invalidateAllUndos();
    this.groupErrorVisible = false;
    this.serverRejectedCodes.clear();
    this.rows.clear({ emitEvent: false });
    const entries = value ?? [];
    for (const entry of entries) {
      this.rows.push(this.createRowGroup(entry), { emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (val: LanguageEntry[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.rows.disable({ emitEvent: false });
    } else {
      this.rows.enable({ emitEvent: false });
    }
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.rows.length === 0) return { required: true };
    const hasInvalid = this.rows.controls.some((row) => row.invalid);
    if (hasInvalid) return { invalidRows: true };
    const hasC2 = this.rows.controls.some(
      (row) => (row.get('level')?.value as LanguageLevelOption)?.code === 'C2',
    );
    if (!hasC2) return { missingNative: true };
    const codes = this.rows.controls
      .map((row) => (row.get('language')?.value as LanguageOption)?.code)
      .filter(Boolean);
    const hasDuplicates = new Set(codes).size !== codes.length;
    if (hasDuplicates) return { duplicateLanguage: true };
    return null;
  }

  markServerUnknownLanguages(codes: string[]): void {
    codes.forEach((code) => this.serverRejectedCodes.add(code));
    for (const row of this.rows.controls) {
      const control = row.get('language');
      const code = (control?.value as LanguageOption | null)?.code;
      if (!control || !code || !codes.includes(code)) continue;
      control.updateValueAndValidity({ emitEvent: false });
      control.markAsTouched();
    }
    this.onValidatorChange();
    this.cdr.markForCheck();
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(I18N + key, params);
  }

  private invalidateAllUndos(): void {
    this.undoSnackBar?.dismiss();
    this.undoSnackBar = null;
    this.undoInvalidators.forEach((fn) => fn());
    this.undoInvalidators.clear();
  }

  private loadLanguages(): void {
    if (this.languagesLoaded || this.languagesLoading) return;
    this.languagesLoading = true;
    this.languagesSub = this.supportedLanguages.getSupportedLanguages().subscribe({
      next: (codes) => {
        this.languagesLoading = false;
        this.languagesLoaded = true;
        this.supportedCodes = codes;
        this.sortOptions(this.translateService.currentLang ?? 'de');
        this.reevaluateRows();
      },
      error: () => {
        this.languagesLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private sortOptions(lang: string): void {
    this.languageOptions = sortLanguageOptions(
      this.supportedCodes,
      code => languageDisplayName(code, key => this.translateService.instant(key), lang),
      lang,
    );
  }

  /** Nur eine geladene, nicht leere Liste entscheidet, ob ein gespeichertes Kürzel unbekannt ist. */
  private toLanguageOption(code: string): LanguageOption {
    const known = this.languageOptions.find((o) => o.code === code);
    if (known) return known;
    const option: LanguageOption = { code, labelKey: languageLabelKey(code) };
    return this.languagesLoaded && this.supportedCodes.length > 0
      ? { ...option, unknown: true }
      : option;
  }

  private reevaluateRows(): void {
    for (const row of this.rows.controls) {
      const control = row.get('language');
      const code = (control?.value as LanguageOption | null)?.code;
      if (!control || !code) continue;
      control.setValue(this.toLanguageOption(code), { emitEvent: false });
    }
    this.onValidatorChange();
    this.cdr.markForCheck();
  }

  private createRowGroup(entry: Partial<LanguageEntry>): FormGroup {
    const languageOption = entry.language ? this.toLanguageOption(entry.language) : null;
    const levelOption = entry.level
      ? (this.levelOptions.find((o) => o.code === entry.level) ?? null)
      : null;

    const unknownLanguageValidator = (ctrl: AbstractControl): ValidationErrors | null =>
      (ctrl.value as LanguageOption)?.unknown ? { unknownLanguage: true } : null;

    const serverRejectedValidator = (ctrl: AbstractControl): ValidationErrors | null =>
      this.serverRejectedCodes.has((ctrl.value as LanguageOption)?.code)
        ? { serverUnknownLanguage: true }
        : null;

    const group = this.fb.group({
      language: [
        languageOption,
        [Validators.required, unknownLanguageValidator, serverRejectedValidator],
      ],
      level: [levelOption, Validators.required],
    });

    return group;
  }

  private toValue(): LanguageEntry[] {
    return this.rows.controls.map((row) => {
      const lang = row.get('language')?.value as LanguageOption | null;
      const lvl = row.get('level')?.value as LanguageLevelOption | null;
      return {
        language: lang?.code ?? '',
        level: lvl?.code as LanguageLevel | undefined,
      };
    });
  }
}
